import { UserProfileResponse } from '@app/dashboard/models/dashboard.model';
import {
  ApiResponse,
  PaginatedApiResponse,
} from '@shared/models/api-response.models';

export enum LoginSettingTypeEnum {
  /**
   * Password with OTP (SMS/Email)
   */
  PASSWORD_WITH_OTP = 'PASSWORD_WITH_OTP',

  /**
   * Password with OTP and Google Authenticator
   */
  PASSWORD_WITH_OTP_G_AUTH = 'PASSWORD_WITH_OTP_G_AUTH',

  /**
   * Password with OTP and device verification
   */
  PASSWORD_WITH_OTP_DEVICE = 'PASSWORD_WITH_OTP_DEVICE',

  /**
   * Password with OTP, Google Authenticator, and device verification
   */
  PASSWORD_WITH_OTP_G_AUTH_DEVICE = 'PASSWORD_WITH_OTP_G_AUTH_DEVICE',
}

export type IpRestrictionStatus = 'ENABLED' | 'DISABLED';
export type IpRestrictionType = 'SINGLE' | 'RANGE';

export interface GetLoginSettingsResponse {
  currentRule: LoginSettingTypeEnum;
}

export interface UpdateLoginSettingsPayload {
  loginRule: LoginSettingTypeEnum;
}

export interface UpdateLoginSettingsResponse {
  message: string;
  requestId: string;
  status: string;
}

export interface IpRuleSummary {
  type: IpRestrictionType;
  enabled: number;
  disabled: number;
  total: number;
}

export interface IpUser {
  id: string;
  firstname: string;
  lastname: string;
}

export interface IpWhitelistItem {
  userId: string | null;
  user?: IpUser;
  status: IpRestrictionStatus;
  rules: IpRuleSummary[];
}

/**
 * `GET /api/ip-whitelist/v1` — paginated.
 *
 * The legacy shape exposed `page`/`limit`/`total`/`totalPages` at the root
 * of the body. Now that we've adopted the standard `ApiResponse<T>` envelope,
 * those fields live under `pagination`. Consumers should read
 * `response.pagination?.{page,limit,total,totalPages}`.
 */
export type IpWhitelistListResponse = PaginatedApiResponse<IpWhitelistItem>;

/** A range as decision D9.5 carries it on the wire. */
export interface IpRangeValue {
  start: string;
  end: string;
}

/**
 * One whitelist entry's address. A single address is a string; a range is
 * `"start,end"` today and `{ start, end }` once user-management adopts D9.5.
 * Read ranges only through `readIpRange` (`utils/ip-range.util.ts`).
 */
export type IpAddressValue = string | IpRangeValue;

export interface IpAddress {
  ip: IpAddressValue;
  status: IpRestrictionStatus;
}

export interface IpRule {
  type: IpRestrictionType;
  status: IpRestrictionStatus;
  ips: IpAddress[];
}

export interface IpWhitelistDetail {
  userId: string | null;
  user?: IpUser;
  status: IpRestrictionStatus;
  rules: IpRule[];
}

/** `GET /api/ip-whitelist/v1/:userId`. */
export type IpWhitelistDetailResponse = ApiResponse<IpWhitelistDetail>;

export interface CreateIpRestrictionRule {
  ip: string;
  status: 'ENABLED' | 'DISABLED';
}

export interface CreateIpRestrictionPayload {
  rules: CreateIpRestrictionRule[];
}

export interface CreateIpRestrictionResponse {
  requestId: string;
  status: 'PENDING' | 'SUCCESS' | 'FAILED';
  userId: string;
  rulesCount: number;
}

export interface UpdateIpRestrictionStatusPayload {
  status: 'ENABLED' | 'DISABLED';
}

export interface UpdateIpRestrictionStatusResponse {
  message: string;
  requestId: string;
  status: string;
}

/** `GET /api/users` — paginated user list. */
export type UserListResponse = PaginatedApiResponse<UserProfileResponse>;
